import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/forms/Input';
import Select from '../../components/forms/Select';
import { useLeads } from '../../hooks/api/useLeads';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { formatDate } from '../../utils/date';

const statusOptions = [
  { value: '', label: 'All statuses' },
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'converted', label: 'Converted' },
  { value: 'unsubscribed', label: 'Unsubscribed' },
];

const formTypeOptions = [
  { value: '', label: 'All types' },
  { value: 'contact', label: 'Contact' },
  { value: 'demo', label: 'Demo' },
  { value: 'newsletter', label: 'Newsletter' },
];

const statusColors: Record<string, string> = {
  new: 'bg-blue-100 text-blue-700',
  contacted: 'bg-yellow-100 text-yellow-700',
  qualified: 'bg-purple-100 text-purple-700',
  converted: 'bg-green-100 text-green-700',
  unsubscribed: 'bg-gray-100 text-gray-700',
};

const LeadManagement: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [formType, setFormType] = useState('');

  const { data: leads, isLoading } = useLeads({
    status: statusFilter || undefined,
    form_type: formType || undefined,
    search: search || undefined,
  });

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-display font-bold text-gray-900">Lead Management</h1>
            <p className="text-sm text-gray-500 mt-1">
              {leads?.length ?? 0} leads captured from the marketing website
            </p>
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search by name, email, or school..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                icon={<MagnifyingGlassIcon className="w-5 h-5 text-gray-400" />}
              />
            </div>
            <div className="w-full sm:w-48">
              <Select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                options={statusOptions}
              />
            </div>
            <div className="w-full sm:w-48">
              <Select
                value={formType}
                onChange={e => setFormType(e.target.value)}
                options={formTypeOptions}
              />
            </div>
          </div>
        </Card>

        {/* Table */}
        <Card className="overflow-hidden !p-0">
          {isLoading ? (
            <div className="p-8 text-center text-gray-500">Loading leads...</div>
          ) : !leads || leads.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No leads found. Leads will appear here when visitors submit forms on the marketing website.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">School</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {leads.map(lead => (
                    <tr
                      key={lead.id}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/admin/leads/${lead.id}`)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <p className="text-sm font-medium text-gray-900">
                          {lead.first_name || lead.last_name
                            ? `${lead.first_name || ''} ${lead.last_name || ''}`.trim()
                            : '—'}
                        </p>
                        {lead.role && (
                          <p className="text-xs text-gray-500 capitalize">{lead.role}</p>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {lead.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {lead.school_name || '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-xs capitalize text-gray-500">{lead.form_type}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full capitalize ${
                          statusColors[lead.status] || 'bg-gray-100 text-gray-700'
                        }`}>
                          {lead.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(lead.created_date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <Button variant="ghost" size="sm">View</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </motion.div>
    </div>
  );
};

export default LeadManagement;
